import { Button } from "@base/ui/Button/Button";
import { membershipQueries } from "@features/membership/api/queryFactory";
import { MembershipPlan } from "@features/membership/ui/MembershipPlan";
import { mdiCog } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface MembershipAndPaymentInfoBoxProps {
  onSettingsClick?: () => void;
  onApplyClick?: () => void;
}

const MembershipAndPaymentInfoBox = ({ onSettingsClick, onApplyClick }: MembershipAndPaymentInfoBoxProps) => {
  const { t } = useTranslation();
  const { data: membership } = useQuery(membershipQueries.membership());
  const { data: autoPayData } = useQuery({
    ...membershipQueries.autoPay(),
    enabled: !!membership,
  });
  const hasMembership = membership?.status === "ACTIVE" && !!autoPayData;

  return (
    <div className="flex flex-col gap-4 p-4 max-w-full sm:max-w-[312px] md:max-w-[400px] rounded-8 border border-gray-200 bg-white">
      <div className="flex justify-between items-center">
        <h3 className="text-title5 font-semibold text-gray-1000 leading-[130%] tracking-[-0.4px]">{t("plan")}</h3>
        {hasMembership && (
          <button
            type="button"
            onClick={onSettingsClick}
            className="p-1 -m-1 hover:bg-gray-100 rounded-4"
            aria-label={t("plan")}
          >
            <Icon path={mdiCog} size="1.5rem" className="text-gray-500" />
          </button>
        )}
      </div>

      {hasMembership ? (
        <>
          <MembershipPlan
            type={membership.plan_description.type}
            billing_cycle={membership.plan_description.billing_cycle}
            amount={membership.plan_description.amount}
          />
          <div className="flex flex-wrap gap-y-2 gap-4">
            <div className="flex flex-col flex-grow gap-1">
              <span className="text-gray-500 text-body4 leading-[150%] tracking-[-0.28px]">{t("nextPaymentDate")}</span>
              <span className="text-subtitle3 font-semibold text-gray-1000 leading-[150%] tracking-[-0.32px]">
                {/* Day Pass는 정기결제가 없으므로 값이 있어도 항상 "-" 표기 */}
                {membership.plan_description.type === "DAY_PASS" ? "-" : membership.next_payment_date || "-"}
              </span>
            </div>
            <div className="flex flex-col flex-grow gap-1">
              <span className="text-gray-500 text-body4 leading-[150%] tracking-[-0.28px]">{t("paymentMethod")}</span>
              <span className="text-subtitle3 font-semibold text-gray-1000 leading-[150%] tracking-[-0.32px]">
                {t("cardLast4", { last4: autoPayData?.method_last4 ?? "" })}
              </span>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-4 items-center py-4">
          <p className="text-center text-gray-500 text-body3">{t("notSubscribedToPlan")}</p>
          <Button type="button" variant="outline" color="primary" className="w-full" onClick={onApplyClick}>
            {t("applyNow")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MembershipAndPaymentInfoBox;
